import { useState, useMemo, useEffect } from 'react';
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { Box, Button, Card, CardContent } from '@mui/material';
import { formatDate, parseDateAndTime } from '../../helpers/UtilityHelper';
import { delivered, deliveryFailed, rto, transit, placed, formatCurrency } from '../../common/util';
import OrderStatusFilter, { OrderStatusChips, orderStatusClause } from './OrderStatusFilter';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFSelectField, RHFTextField } from "../../common/hook-form";
import { useGetAllCourierPartnersQuery } from '../../redux/commonApiCalls';
import SyncIcon from '@mui/icons-material/Sync';
import UpdateIcon from '@mui/icons-material/Update';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useNdr from "../../hooks/useNdr";
import Loader from '../../common/Loader';
import { apiConstants, get } from '../../common/constants';
import { useSnackbar } from 'notistack';
import ManualStatus from './ManualStatus';
import MUIModal from '../../components/MuiModal';
import { Link } from 'react-router-dom';
import { CourierFilter, MerchantFilterCached } from '../../common/ProshipFormComponents';
import PageTitle from '../../components/PageTitle';
import { NON_ABD_FILTER } from './nonAbdFilter';
import StatusChip from '../../common/StatusChip';
import PaymentBadge from '../../common/PaymentBadge';
import OrderRowActions from '../../common/OrderRowActions';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);

const DataLabel = ({ children }) => (
    <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
        {children}
    </Box>
);

// Open Shipments offers the same statuses as before (no Lost / Cancelled: those shipments are closed).
const OPEN_SHIPMENT_STATUSES = [...placed, ...transit, ...delivered, ...deliveryFailed, ...rto];

// courierPartner/search requires a FilterQueryWrapper body — calling it with no args sends an
// empty POST and the API returns 400. handleSync only needs these flags to pick the resync API.
const COURIER_LOOKUP_REQUEST = { offset: 0, limit: 15000, skipCount: true, includes: 'prozoId,trackingOnly,prozoInternal' };

// ─── Component ────────────────────────────────────────────────────────────────

const OpenShipment = ({ nonAbdOnly = false }) => {
    const methods = useForm({
        defaultValues: {
            status: [], courier: null, merchant: '', reference: '',
            awb: '', city: '', state: '', fromDate: '', toDate: '', channel: '', sla: ''
        }
    });
    const { handleSubmit, reset } = methods;

    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [rowCountState, setRowCountState] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = useState(false);
    const [id, setId] = useState();
    const { data: courier = [] } = useGetAllCourierPartnersQuery(COURIER_LOOKUP_REQUEST);
    const self = [{ label: "TRUE", value: "Selfship" }, { label: "FALSE", value: "-1" }];

    const request = useMemo(() => {
        const exclude = "courier,merchant,version,order_history,itemList,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,actualCourierProviderName,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,channelName,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchantPricing.pricingObj,merchantPricing.merchantRateCard,modifiedByUser,lastModifiedDate,terminalStatus";
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "lifeCycleComplete", operator: "ne", value: true });
        andfilter.push({ "field": "", operator: "eq", value: "B2B" });
        andfilter.push({ "field": "shipmentType", operator: "eq", value: "B2B" });
        if (nonAbdOnly) andfilter.push(NON_ABD_FILTER);
        const statusClause = orderStatusClause(filters.status);
        if (statusClause) andfilter.push(statusClause);
        if (filters.awb) andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        if (filters.city) andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        if (filters.state) andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        if (filters.merchant) andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        if (filters.courier) andfilter.push({ "field": "courier.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.courier + '\')' });
        if (filters.reference) { orFilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" }); orFilter.push({ "field": "shippingLabelId", "operator": "like", "value": filters.reference + ".*" }); }
        if (filters.fromDate) andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        if (filters.toDate) andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        if (filters.channel) andfilter.push({ field: "channelName", operator: "eq", value: "Selfship" });
        return { "andfilter": andfilter, "orfilter": orFilter, "offset": (page) * sizePerPage, "limit": sizePerPage, "skipCount": true, "excludes": exclude };
    }, [page, sizePerPage, filters, nonAbdOnly]);

    const requestCount = useMemo(() => {
        const andfilter = [];
        const orFilter = [];
        andfilter.push({ "field": "lifeCycleComplete", operator: "ne", value: true });
        andfilter.push({ "field": "", operator: "eq", value: "B2B" });
        andfilter.push({ "field": "shipmentType", operator: "eq", value: "B2B" });
        if (nonAbdOnly) andfilter.push(NON_ABD_FILTER);
        const statusClause = orderStatusClause(filters.status);
        if (statusClause) andfilter.push(statusClause);
        if (filters.awb) andfilter.push({ "field": "awbNumber", "operator": "eq", "value": filters.awb });
        if (filters.city) andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        if (filters.state) andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        if (filters.merchant) andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        if (filters.courier) andfilter.push({ "field": "courier.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.courier + '\')' });
        if (filters.reference) { orFilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" }); orFilter.push({ "field": "shippingLabelId", "operator": "like", "value": filters.reference + ".*" }); }
        if (filters.fromDate) andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        if (filters.toDate) andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        if (filters.channel) andfilter.push({ field: "channelName", operator: "eq", value: "Selfship" });
        return { "andfilter": andfilter, "orfilter": orFilter, "offset": (page) * sizePerPage, "limit": sizePerPage, "includes": "id" };
    }, [page, sizePerPage, filters, nonAbdOnly]);

    let { data = [], isFetching, isLoading } = useNdr({ request: request });
    const { data: dataCount } = useNdr({ request: requestCount });

    const handleSync = (row) => {
        const trackOnly = courier?.filter(item => item?.prozoId === row?.courierDetail?.prozoId);
        if (trackOnly[0]?.trackingOnly === true && trackOnly[0]?.prozoInternal === true) {
            get(`${apiConstants.B2B_RESYNC}/${row?.awb_number}`).then((res) => {
                enqueueSnackbar(res.status === 200 ? 'Response: Order has been updated' : 'Response: Order is not updated', { variant: res.status === 200 ? 'success' : 'error' });
            }).catch(err => console.log(err));
        } else {
            setLoading(true);
            get(`${apiConstants.RESYNC_ORDER}?waybill=${row?.awb_number}&reference=${row?.reference}`).then((res) => {
                enqueueSnackbar(res.status === 200 ? 'Response: Order has been updated' : 'Response: Order is not updated', { variant: res.status === 200 ? 'success' : 'error' });
            }).catch(err => console.log(err)).finally(() => setLoading(false));
        }
    };

    const handleClickOpen = (row) => { setOpen(true); setId(row); };
    const handleClose = () => setOpen(false);

    useEffect(() => {
        setRowCountState(prev => dataCount?.count !== undefined ? dataCount?.count : prev);
    }, [dataCount?.count]);

    const onSubmit = (data) => { setPage(0); setFilters(data); };

    const columns = [
        {
            field: 'merchant',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>,
        },
        {
            field: 'actions',
            headerName: 'Order Details', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 1.2,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.reference || '—'}</CellLine>
                    {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                        <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>{row.shippingLabelId}</Box>
                    )}
                    {row.order_date && <Box sx={{ fontSize: '11px', color: '#9ca3af' }}>{parseDateAndTime(row.order_date)}</Box>}
                </CellStack>
            ),
        },
        {
            field: 'awb',
            headerName: 'AWB', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 160, flex: 1.1,
            renderCell: ({ row }) => row.awb_number ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        component={Link}
                        to={`/order-detail/${row.awb_number}`}
                        sx={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd', pb: '1px', '&:hover': { color: '#1e40af', borderBottomStyle: 'solid' } }}
                    >
                        {row.awb_number}
                    </Box>
                    <CopyIcon text={row.awb_number} />
                </Box>
            ) : <CellLine muted>—</CellLine>,
        },
        {
            field: 'pickup',
            headerName: 'Pickup', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 120,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.pickup_details?.from_city && <CellLine bold>{row.pickup_details.from_city}</CellLine>}
                    <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.pickup_details?.from_pincode}</Box>
                </CellStack>
            ),
        },
        {
            field: 'drop',
            headerName: 'Drop', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 120,
            renderCell: ({ row }) => (
                <CellStack>
                    {row.delivery_details?.to_city && <CellLine bold>{row.delivery_details.to_city}</CellLine>}
                    <Box sx={{ fontSize: '11px', color: '#6b7280' }}>{row.delivery_details?.to_pincode}</Box>
                </CellStack>
            ),
        },
        {
            field: 'courier',
            headerName: 'Courier', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row?.courierDetail?.parent || '—'}</CellLine>
                    {row?.courierDetail?.prozoId && <CellLine muted ellipsis>{row.courierDetail.prozoId}</CellLine>}
                </CellStack>
            ),
        },
        {
            field: 'statusDetails',
            headerName: 'Status', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => <StatusChip status={row?.orderStatus} />,
        },
        {
            field: 'edd',
            headerName: 'EDD', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.9,
            renderCell: ({ row }) => <CellLine muted>{row.edd ? parseDateAndTime(row.edd) : '—'}</CellLine>,
        },
        {
            field: 'invoice',
            headerName: 'Invoice', headerAlign: 'right', align: 'right',
            hideSortIcons: true, minWidth: 110, flex: 0.8,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    {row.invoice_value ? formatCurrency(row.invoice_value) : '—'}
                </Box>
            ),
        },
        {
            field: 'action',
            headerName: 'Actions', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        { key: 'view', label: 'View details', icon: <VisibilityIcon fontSize="small" />, href: `/order-detail/${row.awb_number}` },
                        { key: 'resync', label: 'Resync status', icon: <SyncIcon fontSize="small" />, onClick: () => handleSync(row), dividerBefore: true },
                        { key: 'manual', label: 'Manual status update', icon: <UpdateIcon fontSize="small" />, onClick: () => handleClickOpen(row) },
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>{nonAbdOnly ? 'Open Non-ABD Shipments' : 'Open Shipment'}</PageTitle>
            {(isFetching || isLoading || loading) && <Loader />}

            <MUIModal
                open={open}
                handleClose={handleClose}
                children={<ManualStatus id={id} setFilters={setFilters} setOpen={setOpen} />}
                title={`Please provide detail of AWB ${id?.awb_number} for Manual status update`}
            />

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <CourierFilter filterList={"B2B"} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Order Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Order Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <FilterField>
                                <FieldLabel>Self Ship</FieldLabel>
                                <RHFSelectField name="channel" label="" placeholder="Select Self Ship" width="100%" size="small" displayEmpty
                                    options={self.map(v => ({ value: v.value, label: v.label }))} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>SLA Breach</FieldLabel>
                                <RHFSelectField name="sla" label="" placeholder="Select SLA Breach" width="100%" size="small" displayEmpty
                                    options={self.map(v => ({ value: v.value, label: v.label }))} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Order Status</FieldLabel>
                                <OrderStatusFilter statuses={OPEN_SHIPMENT_STATUSES} />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset({ status: [], courier: '', merchant: '', reference: '', awb: '', city: '', state: '', fromDate: '', toDate: '', channel: '', sla: '' }); setFilters([]); setPage(0); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                        <OrderStatusChips applied={filters.status} onApply={onSubmit} />
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={(data?.data && data?.data.length > 0) ? data?.data : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={rowCountState}
                    getRowId={(row) => row.id}
                />
            </Box>
        </>
    );
};

export default OpenShipment;
